import { useState, useMemo } from 'react';
import { CheckCircle2, Sun, Calendar, Trash2, ArrowRight, Clock } from 'lucide-react';

export default function TodayWrapUp({
  todayTasks = [],
  onComplete,
  onDeferToTomorrow,
  onSchedule,
  onDelete
}) {
  const [processingId, setProcessingId] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  // Filter to only incomplete tasks
  const incompleteTasks = useMemo(() => {
    return todayTasks.filter(t => !t.completedAt);
  }, [todayTasks]);

  // Count completed today
  const completedCount = useMemo(() => {
    return todayTasks.filter(t => t.completedAt).length;
  }, [todayTasks]);

  const handleAction = async (action, taskId, ...args) => {
    setProcessingId(taskId);
    try {
      await action(taskId, ...args);
    } finally {
      setProcessingId(null);
    }
  };

  const handleScheduleClick = (taskId) => {
    setShowDatePicker(taskId);
    // Default to day after tomorrow
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    setSelectedDate(dayAfterTomorrow.toISOString().split('T')[0]);
  };

  const handleScheduleConfirm = async (taskId) => {
    if (selectedDate && onSchedule) {
      await handleAction(onSchedule, taskId, selectedDate);
    }
    setShowDatePicker(null);
    setSelectedDate('');
  };

  // Empty state - all tasks done!
  if (incompleteTasks.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <CheckCircle2 size={32} className="text-success" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Today's Tasks Complete!
        </h3>
        <p className="text-sm text-text-tertiary max-w-xs">
          {completedCount > 0
            ? `Great job! You completed ${completedCount} task${completedCount !== 1 ? 's' : ''} today.`
            : "No tasks were scheduled for today. You're all set!"
          }
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-surface-card border-b border-border flex items-center gap-2">
        <Sun size={18} className="text-amber-400" />
        <span className="text-sm font-semibold text-text-primary">
          Wrap Up Today
        </span>
        <span className="text-xs text-text-tertiary ml-2">
          {incompleteTasks.length} incomplete · {completedCount} completed
        </span>
      </div>

      {/* Description */}
      <div className="px-4 py-3 bg-surface-hover/50 border-b border-border">
        <p className="text-xs text-text-secondary">
          Review today's incomplete tasks. Mark them done, defer to tomorrow, or schedule for later.
        </p>
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {incompleteTasks.map((task) => (
          <div
            key={task.id}
            className={`
              p-4 bg-surface-card rounded-xl border border-border
              transition-all duration-200
              ${processingId === task.id ? 'opacity-50 scale-[0.98]' : ''}
            `}
          >
            {/* Task info */}
            <div className="mb-3">
              <h4 className="text-sm font-medium text-text-primary mb-1">
                {task.title}
              </h4>
              {task.notes && (
                <p className="text-xs text-text-tertiary line-clamp-2">
                  {task.notes}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {task.gcalTimeBlockName && (
                  <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded">
                    {task.gcalTimeBlockName}
                  </span>
                )}
                {task.timeEstimate && (
                  <span className="text-[10px] text-text-muted flex items-center gap-1">
                    <Clock size={10} />
                    {task.timeEstimate} min
                  </span>
                )}
                {task.context && (
                  <span className="text-[10px] text-text-tertiary">
                    {task.context}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mark Done */}
              <button
                onClick={() => handleAction(onComplete, task.id)}
                disabled={processingId === task.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border-none cursor-pointer transition-all bg-success/10 text-success hover:bg-success/20 disabled:opacity-50"
              >
                <CheckCircle2 size={14} />
                Done
              </button>

              {/* Defer to Tomorrow */}
              <button
                onClick={() => handleAction(onDeferToTomorrow, task.id)}
                disabled={processingId === task.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border-none cursor-pointer transition-all bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-50"
              >
                <ArrowRight size={14} />
                Tomorrow
              </button>

              {/* Schedule - with date picker */}
              {showDatePicker === task.id ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="px-2 py-1 text-xs rounded-lg border border-border bg-surface-base text-text-primary"
                  />
                  <button
                    onClick={() => handleScheduleConfirm(task.id)}
                    disabled={!selectedDate || processingId === task.id}
                    className="px-2 py-1.5 text-xs font-medium rounded-lg border-none cursor-pointer bg-accent text-white hover:bg-orange-hover disabled:opacity-50"
                  >
                    Set
                  </button>
                  <button
                    onClick={() => setShowDatePicker(null)}
                    className="px-2 py-1.5 text-xs rounded-lg border-none cursor-pointer text-text-muted hover:text-text-primary bg-transparent"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleScheduleClick(task.id)}
                  disabled={processingId === task.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border-none cursor-pointer transition-all bg-gray-500/10 text-text-secondary hover:bg-gray-500/20 disabled:opacity-50"
                >
                  <Calendar size={14} />
                  Schedule
                </button>
              )}

              {/* Delete - icon only */}
              <button
                onClick={() => handleAction(onDelete, task.id)}
                disabled={processingId === task.id}
                className="p-1.5 rounded-lg border-none cursor-pointer transition-all text-text-muted bg-transparent hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 ml-auto"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
